import has from 'transmute/has';
import FullPageError from '../../../errorBoundary/FullPageError';
import { useFetchAllData } from '../hooks/useFetchAllData';
import PageLoadingSpinner from './PageLoadingSpinner';
import { UnavailableObjectTypeError } from '../../../errorBoundary/UnavailableObjectTypeError';
import { ProvideObjectTypeDefinitions } from '../../../crmObjects/context/ObjectTypeDefinitionsContext';
import { ProvidePipelines } from '../../pipelines/context/PipelinesContext';
import MissingViewError from '../../../errorBoundary/MissingViewError';
import { useCallback, useEffect, useMemo } from 'react';
import { useStoreViewsActions } from '../../../views/hooks/useStoreViewsActions';
import { useViews } from '../../../views/hooks/useViews';
import { useDeletedViewIds } from '../../../views/hooks/useDeletedViewIds';
import isEmpty from 'transmute/isEmpty';
import { ProvideAppSettings } from '../../../frameworkAppSettings/context/AppSettingsContext';
import useMarkOnMount from 'crm-fe-perf/timing/hooks/useMarkOnMount';
import { IndexDataLoaderMounted } from 'crm-fe-perf/timing/marks/crm-index-ui/performanceMarks';
import { isNetworkErrorResponse } from '../../../apollo/types/NetworkErrorResponse';
import { useSelectedObjectTypeId } from '../../../objectTypeIdContext/hooks/useSelectedObjectTypeId';
import { getAdditionalMetricsDimensions } from '../../../lib/metrics/getAdditionalMetricsDimensions';
import { useCurrentPageType } from '../../../views/hooks/useCurrentPageType';
import { ProvideTableColumnValidation } from '../../../app/components/IndexPageTable/useTableColumns';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
// TODO: We should ask the BE to give us more descriptive errors when this happens
export const didGraphqlFailBecauseOfMissingObjectTypeId = dataQueryError => Boolean(dataQueryError && dataQueryError.graphQLErrors && dataQueryError.graphQLErrors.find(error => error.message.includes('resource not found') || error.message.includes('Unable to infer object type')));

// Check if the fetch all view hook returned an error indicating that the object is not supported.
// Note: This is the way we decided to check if an object is not supported. Another option we
// considered was checking for the specific graphQL error, but it required to make assumptions
// on the response error since it was not very helpful, or to request the backend/graphQL teams
// to send a more descriptive error message, a longer process we avoided with this approach.
export const didViewApiFailBecauseOfObjectTypeNotSupported = viewError => {
  var _viewError$networkErr;
  return isNetworkErrorResponse(viewError) && Boolean((_viewError$networkErr = viewError.networkError.responseJSON) === null || _viewError$networkErr === void 0 || (_viewError$networkErr = _viewError$networkErr.message) === null || _viewError$networkErr === void 0 ? void 0 : _viewError$networkErr.includes('Cannot get object type'));
};

// HACK: This is a component because there is no step in the init flow that both
// has access to the app's data contexts and doesn't depend on the views
// already existing in state. Once we've backfilled the BE to the new view format,
// we can remove this component.
const CacheViews = ({
  currentView,
  children
}) => {
  const viewsInState = useViews();
  const deletedViewIds = useDeletedViewIds();
  const shouldCache = useCallback(view => view && !has(view.id, viewsInState) && !deletedViewIds.has(view.id), [viewsInState, deletedViewIds]);
  const mergedViews = useMemo(() => Object.assign({}, currentView && shouldCache(currentView) ? {
    [currentView.id]: currentView
  } : {}), [currentView, shouldCache]);
  const hasViewsToCache = !isEmpty(mergedViews);
  const {
    storeViews
  } = useStoreViewsActions();
  useEffect(() => {
    if (hasViewsToCache) {
      storeViews(mergedViews);
    }
  }, [hasViewsToCache, mergedViews, storeViews]);
  return hasViewsToCache ? /*#__PURE__*/_jsx(PageLoadingSpinner, {}) : /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};
const emptyPipelinesArray = [];
export const IndexDataLoader = ({
  children
}) => {
  const objectTypeId = useSelectedObjectTypeId();
  const pageType = useCurrentPageType();
  useMarkOnMount(IndexDataLoaderMounted, getAdditionalMetricsDimensions(objectTypeId, pageType));
  const {
    appSettings,
    loading,
    error,
    pipelines,
    objectTypes,
    // TODO: Unroll `useFetchAllData` into this component, as it is no longer reused.
    escapeHatch: {
      currentView,
      currentViewError
    }
  } = useFetchAllData();
  if (loading || !objectTypes) {
    return /*#__PURE__*/_jsx(PageLoadingSpinner, {});
  }

  // This must be checked above the standard error case because a missing objectType
  // will cause useFetchAllData to return error: true. If it was called after, the
  // less-specific error would take precedence and users would never see the 404.
  if (didViewApiFailBecauseOfObjectTypeNotSupported(currentViewError)) {
    return /*#__PURE__*/_jsx(UnavailableObjectTypeError, {
      error: new Error('Object type not supported')
    });
  }
  if (currentViewError) {
    return /*#__PURE__*/_jsx(MissingViewError, {});
  }
  if (error) {
    return /*#__PURE__*/_jsx(FullPageError, {
      error: error
    });
  }
  return /*#__PURE__*/_jsx(ProvideObjectTypeDefinitions, {
    rawValue: objectTypes,
    children: /*#__PURE__*/_jsx(ProvidePipelines, {
      rawValue: pipelines || emptyPipelinesArray,
      children: /*#__PURE__*/_jsx(ProvideAppSettings, {
        rawValue: appSettings,
        children: /*#__PURE__*/_jsx(CacheViews, {
          currentView: currentView,
          children: /*#__PURE__*/_jsx(ProvideTableColumnValidation, {
            children: children
          })
        })
      })
    })
  });
};
export default IndexDataLoader;